// admin/functions/generatePollDraftAI.js
// AI poll-draft generation, calling OUR backend proxy (/generateAiQuestions)
// — same architecture as generateQuizDraftAI.js (prompt building, lenient
// JSON recovery, batching, top-up retries), adapted to the Poll schema:
// question_text + options only (no correct answer).
//
// Output is always an EDITABLE DRAFT — never auto-published.

const AI_BATCH_SIZE = 10;
const MAX_EMPTY_RETRIES = 3;

function buildPrompt({ topic, count, optionCount, excludeTexts }) {
  const exclude =
    excludeTexts && excludeTexts.length
      ? `\n\nEXCLUDE LIST — do not repeat any of these questions (reword or pick entirely different ones):\n` +
        excludeTexts.map((t) => `- ${t}`).join("\n")
      : "";

  return (
    `You are a JSON generator. Respond with ONLY a valid JSON array — no markdown, no code fences, no explanations, no extra text. ` +
    `Generate exactly ${count} single-select POLL question objects about "${topic}" suitable for a live audience poll (opinion/preference style, no single correct answer). ` +
    `Each poll question must have exactly ${optionCount} distinct options, each under 150 characters. ` +
    `Each object must have exactly these fields: question_text (string, under 300 characters), options (array of ${optionCount} strings). ` +
    `Vary the phrasing across questions. Do not duplicate any question within this batch.` +
    `${exclude}\n\n` +
    `Respond with only the JSON array. Example shape:\n` +
    `[{"question_text":"...","options":["...","..."]}]`
  );
}

function stripFences(raw) {
  return String(raw || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .replace(/\\_/g, "_")
    .trim();
}

// Scan char-by-char for balanced {...} blocks so one malformed object
// doesn't take down the whole batch.
function extractJsonObjects(text) {
  const objects = [];
  let depth = 0;
  let start = -1;
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (escapeNext) {
      escapeNext = false;
      continue;
    }
    if (ch === "\\") {
      escapeNext = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (ch === "{") {
      if (depth === 0) start = i;
      depth += 1;
    } else if (ch === "}") {
      depth -= 1;
      if (depth === 0 && start !== -1) {
        const candidate = text.slice(start, i + 1);
        try {
          objects.push(JSON.parse(candidate));
        } catch (e) {
          // skip malformed object, keep scanning
        }
        start = -1;
      }
    }
  }
  return objects;
}

function parseAiResponse(raw) {
  const cleaned = stripFences(raw);

  if (cleaned.startsWith("[") && cleaned.endsWith("]")) {
    try {
      return JSON.parse(cleaned);
    } catch (e) {
      // fall through
    }
  }

  const arrayMatch = cleaned.match(/\[[\s\S]*\]/);
  if (arrayMatch) {
    try {
      return JSON.parse(arrayMatch[0]);
    } catch (e) {
      // fall through
    }
  }

  const recovered = extractJsonObjects(cleaned);
  if (recovered.length) return recovered;

  throw new Error("AI did not return a parseable JSON array");
}

function normalizePollQuestion(raw) {
  const questionText = String(raw.question_text || "").trim();
  const options = (raw.options || []).map((o) => String(o || "").trim()).filter(Boolean);
  if (!questionText || options.length < 2) return null;
  return { question_text: questionText, options };
}

async function callAiProxy(prompt, token) {
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/generateAiQuestions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ prompt }),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json.success === false) {
    throw new Error(json.message || `AI proxy request failed (${response.status})`);
  }
  return json.response;
}

async function fetchAiBatch({ topic, count, optionCount, excludeTexts, token }) {
  const prompt = buildPrompt({ topic, count, optionCount, excludeTexts });
  const raw = await callAiProxy(prompt, token);
  const parsed = parseAiResponse(raw);

  const excludeSet = new Set(excludeTexts.map((t) => t.toLowerCase()));
  const seen = new Set();
  const results = [];

  for (const item of parsed) {
    const q = normalizePollQuestion(item);
    if (!q) continue;
    const key = q.question_text.toLowerCase();
    if (excludeSet.has(key) || seen.has(key)) continue;
    seen.add(key);
    results.push(q);
  }

  return results;
}

/**
 * Generate `count` poll question drafts, batching + retrying short/failed
 * batches until the count is reached or the failure cap is hit.
 *
 * @param {object} args
 * @param {string} args.topic
 * @param {number} args.count
 * @param {number} [args.optionCount=4]
 * @param {string[]} [args.existingTexts]
 * @param {string} args.token
 * @returns {Promise<Array<{question_text:string, options:string[]}>>}
 */
export async function generatePollDraftAI({ topic, count = 3, optionCount = 4, existingTexts = [], token }) {
  const collected = [];
  const excludeTexts = [...existingTexts];
  let remaining = count;
  let emptyStreak = 0;

  while (remaining > 0) {
    const batchCount = Math.min(AI_BATCH_SIZE, remaining);
    let batchResults;
    try {
      batchResults = await fetchAiBatch({ topic, count: batchCount, optionCount, excludeTexts, token });
    } catch (err) {
      emptyStreak += 1;
      if (emptyStreak >= MAX_EMPTY_RETRIES) {
        if (collected.length) return collected;
        throw err;
      }
      continue;
    }

    if (batchResults.length === 0) {
      emptyStreak += 1;
      if (emptyStreak >= MAX_EMPTY_RETRIES) break;
      continue;
    }

    emptyStreak = 0;
    collected.push(...batchResults);
    excludeTexts.push(...batchResults.map((q) => q.question_text));
    remaining = count - collected.length;
  }

  return collected.slice(0, count);
}

export default generatePollDraftAI;
